import { useCallback } from 'react';
import type {
  ReactElement,
  MouseEventHandler,
} from 'react';

import styled from 'styled-components';

import type {
  DropdownMenuItemRenderProps,
  DropdownMenuItemType,
} from './types';

export const StyledMenuItem = styled.button(({
  disabled,

  theme: {
    colors,
    fonts,
    indents,
  },
}) => ({
  ...fonts.DEFAULT,
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  margin: 0,
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: indents.INDENT_2,
  paddingRight: indents.INDENT_2,
  backgroundColor: 'transparent',
  color: disabled ? colors.NEUTRAL_INPUT : colors.NEUTRAL_DEFAULT,
  cursor: disabled ? 'default' : 'pointer',
  outline: 'none',
  border: 'none',
  textAlign: 'left',

  ':hover': disabled ? undefined : {
    backgroundColor: colors.ACTION_BACKGROUND,
    color: colors.NEUTRAL_DEFAULT,
  },

  ':active': disabled ? undefined : {
    backgroundColor: colors.ACTION_BACKGROUND,
    color: colors.NEUTRAL_DEFAULT,
  },
}));

export const StyledIcon = styled.span(({
  theme: {
    indents,
  },
}) => ({
  display: 'inline-block',
  verticalAlign: -3,
  height: 16,
  marginTop: -2,
  marginBottom: -2,
  paddingRight: indents.INDENT_1,
}));

export const defaultRender = (renderProps: DropdownMenuItemRenderProps) => (
  <button
    type="button"
    {...renderProps}
  />
);

export type MenuItemProps = {
  menuItem: DropdownMenuItemType;
  close: () => void;
};

export function MenuItem({
  menuItem: {
    className = '',
    disabled = false,
    label,
    onClick,
    render = defaultRender,
    renderIcon,
  },

  close,
}: MenuItemProps): ReactElement {
  const icon = renderIcon && (
    <StyledIcon>
      {renderIcon({
        disabled,
      })}
    </StyledIcon>
  );

  const onItemClick = useCallback<MouseEventHandler>((event) => {
    if (onClick) {
      onClick(event);
    }

    close();
  }, [onClick, close]);

  const as = useCallback(
    (renderProps: DropdownMenuItemRenderProps) => render(renderProps) as ReactElement,
    [render],
  );

  if (disabled) {
    return (
      <StyledMenuItem
        type="button"
        disabled
        className={className}
      >
        {icon}
        {label}
      </StyledMenuItem>
    );
  }

  return (
    <StyledMenuItem
      as={as}
      className={className}
      onClick={onItemClick}
    >
      {icon}
      {label}
    </StyledMenuItem>
  );
}
